/**
 * Shared constants for historical periods/eras across the application.
 * Used for standardized period classification in Place and PlacesByPassage data.
 */

/**
 * Standard archaeological/historical periods for biblical history
 */
export const HISTORICAL_PERIODS = {
  PRE_POTTERY_NEOLITHIC: 'Pre-Pottery Neolithic',
  POTTERY_NEOLITHIC: 'Pottery Neolithic',
  CHALCOLITHIC: 'Chalcolithic Period',
  EARLY_BRONZE: 'Early Bronze Age',
  MIDDLE_BRONZE: 'Middle Bronze Age',
  LATE_BRONZE: 'Late Bronze Age',
  IRON_I: 'Iron Age I',
  IRON_II: 'Iron Age II',
  NEO_BABYLONIAN: 'Neo-Babylonian Period',
  PERSIAN: 'Persian Period',
  HELLENISTIC: 'Hellenistic Period',
  HASMONEAN: 'Hasmonean Period',
  ROMAN: 'Roman Period',
  BYZANTINE: 'Byzantine Period',
} as const;

/**
 * TypeScript type for historical periods
 */
export type HistoricalPeriod = typeof HISTORICAL_PERIODS[keyof typeof HISTORICAL_PERIODS];

/**
 * Valid historical period values (for validation)
 */
export const VALID_HISTORICAL_PERIODS = Object.values(HISTORICAL_PERIODS);

/**
 * Period definitions with date ranges
 */
export const PERIOD_DEFINITIONS: Record<HistoricalPeriod, { start: number; end: number; description: string }> = {
  'Pre-Pottery Neolithic': {
    start: -10000,
    end: -6500,
    description: 'First permanent settlements, early agriculture, pre-pottery phase',
  },
  'Pottery Neolithic': {
    start: -6500,
    end: -4500,
    description: 'Introduction of pottery, continued agricultural development',
  },
  'Chalcolithic Period': {
    start: -4500,
    end: -3300,
    description: 'Copper Age, transition from stone to metal tools',
  },
  'Early Bronze Age': {
    start: -3300,
    end: -2100,
    description: 'Early urbanization in the Ancient Near East',
  },
  'Middle Bronze Age': {
    start: -2100,
    end: -1550,
    description: 'Patriarchal period, Hyksos in Egypt',
  },
  'Late Bronze Age': {
    start: -1550,
    end: -1200,
    description: 'Egyptian New Kingdom, Amarna period',
  },
  'Iron Age I': {
    start: -1200,
    end: -1000,
    description: 'Israelite settlement, Period of the Judges',
  },
  'Iron Age II': {
    start: -1000,
    end: -586,
    description: 'United and Divided Kingdoms, Assyrian and Babylonian dominance',
  },
  'Neo-Babylonian Period': {
    start: -586,
    end: -539,
    description: 'Babylonian Exile',
  },
  'Persian Period': {
    start: -539,
    end: -332,
    description: 'Persian Empire, Return from Exile',
  },
  'Hellenistic Period': {
    start: -332,
    end: -167,
    description: 'Greek rule, Ptolemaic and Seleucid dynasties',
  },
  'Hasmonean Period': {
    start: -167,
    end: -63,
    description: 'Maccabean revolt and Jewish independence',
  },
  'Roman Period': {
    start: -63,
    end: 324,
    description: 'Roman rule, New Testament era',
  },
  'Byzantine Period': {
    start: 324,
    end: 638,
    description: 'Christian Byzantine Empire',
  },
};

/**
 * Helper function to determine standard period from a year
 * @param year - Year (negative for BC, positive for AD)
 * @returns The standard period name, or undefined if no match
 */
export function getStandardPeriod(year: number): HistoricalPeriod | undefined {
  for (const [period, def] of Object.entries(PERIOD_DEFINITIONS)) {
    if (year >= def.start && year <= def.end) {
      return period as HistoricalPeriod;
    }
  }
  return undefined;
}

/**
 * Helper function to get period date range as display string
 * @param period - The standard period name
 * @returns Display string like "1200-1000 BC" or "63 BC - AD 324"
 */
export function getPeriodDateRange(period: HistoricalPeriod): string {
  const def = PERIOD_DEFINITIONS[period];
  if (!def) return '';

  const formatYear = (year: number): string => {
    if (year < 0) return `${Math.abs(year)} BC`;
    return `AD ${year}`;
  };

  return `${formatYear(def.start)} - ${formatYear(def.end)}`;
}

